import React from "react";
import Lightbox from "../../../ComponentsForked/react-image-lightbox/src";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import SlideData, {UploadStatus} from "../../../Interfaces/SlideData";
import SlidesPopupItem from "./SlidesPopupItem";
import {addSlide, removeSlide} from "../../../store/makerDataSlice";
import {ErrorBoundary} from "../../Common/ErrorBoundary";
import rotatePhoto from "../../../Api/rotatePhoto";
import {useMutation} from "@tanstack/react-query";
import axios from "axios";
import * as Sentry from "@sentry/react";
import addText from "../../../Api/addText";
import ym from "react-yandex-metrika";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {SWAL_DEFAULTS} from "../../../Utils/defaults";
import {Provider} from "react-redux";
import {store} from "../../../store/store";
import EditSlidePopup from "./EditSlidePopup";

interface SlidesPopupProps {
    setCurrentSlide: (data: SlideData) => void,
    onCloseRequest: () => void
}

const SlidesPopup: React.FC<SlidesPopupProps> = ({
                                                     setCurrentSlide,
                                                     onCloseRequest
                                                 }) => {

    const slides = useAppSelector(state => state.makerData.slides)
    const currentSlide = useAppSelector(state => state.makerData.currentSlide)
    const makerData = useAppSelector(state => state.makerData)

    const dispatch = useAppDispatch();

    const mutationRotatePhoto = useMutation({
        mutationFn: rotatePhoto,
        onError: (error, variables, context) => {
            let errorUrl = '';
            if (axios.isAxiosError(error)) {
                errorUrl = String(error.config?.url);
                // @ts-ignore
            } else if (typeof error?.url !== 'undefined') {
                // @ts-ignore
                errorUrl = error?.url;
            }
            Sentry.captureException(
                // @ts-ignore
                new Error((error?.message ? error.message : 'Failed to rotate photo. Generic error.') + ', URL: ' + errorUrl),
                {
                    extra: {
                        variables: variables,
                        error: error,
                        responseConfig: axios.isAxiosError(error) ? JSON.stringify(error.response?.config) : '',
                        responseData: axios.isAxiosError(error) ? JSON.stringify(error.response?.data) : ''
                    }
                }
            )
        },
        retry: 5,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000)
    })

    const mutationAddText = useMutation({
        mutationFn: addText,
        onError: (error, variables, context) => {
            dispatch(addSlide({
                pid: variables.pid,
                localFileName: '',
                type: 'text',
                status: UploadStatus.uploadError,
                uploadError: 'Ошибка при добавлении текста'
            } as SlideData));
            let errorUrl = '';
            if (axios.isAxiosError(error)) {
                errorUrl = String(error.config?.url);
                // @ts-ignore
            } else if (typeof error?.url !== 'undefined') {
                // @ts-ignore
                errorUrl = error?.url;
            }
            Sentry.captureException(
                // @ts-ignore
                new Error((error?.message ? error.message : 'Failed to add text. Generic error.') + ', URL: ' + errorUrl),
                {
                    extra: {
                        variables: variables,
                        error: error,
                        responseConfig: axios.isAxiosError(error) ? JSON.stringify(error.response?.config) : '',
                        responseData: axios.isAxiosError(error) ? JSON.stringify(error.response?.data) : ''
                    }
                }
            )
            ym('reachGoal', 'addTextSlideFail');
        },
        retry: 5,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000)
    })

    const onRotateSlide = async (e: React.MouseEvent<HTMLElement>, slideData: SlideData) => {
        e.stopPropagation();
        mutationRotatePhoto.mutateAsync({
            slideData: slideData,
            dispatch: dispatch,
            makerData: makerData
        }).catch(e => {})
    }

    const onAddTextWithSlideData = (text: string, slideData: SlideData) => {
        mutationAddText.mutateAsync({
            text: text,
            dispatch: dispatch,
            tmpProjectId: makerData.tmpProjectId,
            pid: slideData.pid,
            makerData: makerData
        }).catch(e => {})
    }

    const onEditSlide = (e: React.MouseEvent<HTMLElement>, slideData: SlideData) => {
        e.stopPropagation();

        const MySwal = withReactContent(Swal)

        MySwal.fire({
            ...SWAL_DEFAULTS,
            customClass: {
                container: 'sl-swal sl-swal-add-slide'
            },
            title: "Редактирование текстового слайда",
            html: <Provider store={store}>
                <EditSlidePopup onAddTextWithSlideData={onAddTextWithSlideData} slideData={slideData}></EditSlidePopup>
            </Provider>
            ,
            width: 'auto'
        })
    }

    const onDeleteSlide = (e: React.MouseEvent<HTMLElement>, slideData: SlideData) => {
        e.stopPropagation();
        dispatch(removeSlide(slideData));
    }

    const getCurrentSlidesIndex = (): number => {
        return Math.max(Number(slides.findIndex((value) => {
            return currentSlide?.pid == value.pid
        })), 0);
    }
    const getPrevSlide = (): SlideData => {
        const prevSlide = getCurrentSlidesIndex() == 0
            ? slides[slides.length - 2]
            : slides[getCurrentSlidesIndex() - 1];
        if (prevSlide === undefined)
            return {} as SlideData;
        else
            return prevSlide;
    }
    const getNextSlide = (): SlideData => {
        const nextSlide = getCurrentSlidesIndex() == Number(slides.length) - 2
            ? slides[0]
            : slides[getCurrentSlidesIndex() + 1];
        if (nextSlide === undefined)
            return {} as SlideData;
        else
            return nextSlide;
    }

    return (
        <div>
            {currentSlide ?
                (
                    <Lightbox
                        mainSrc={currentSlide?.src ? currentSlide.src : currentSlide.pid}
                        mainCustomContent={(
                            <ErrorBoundary
                                place={'SlidesPopup-SlidesPopupItem-1'}
                                key={currentSlide?.pid}>
                                <SlidesPopupItem
                                    data={slides[getCurrentSlidesIndex()]}
                                    onDeleteSlide={(e: React.MouseEvent<HTMLElement>) => onDeleteSlide(e, currentSlide)}
                                    onRotateSlide={(e: React.MouseEvent<HTMLElement>) => onRotateSlide(e, currentSlide)}
                                    onEditSlide={(e: React.MouseEvent<HTMLElement>) => onEditSlide(e, currentSlide)}
                                >
                                </SlidesPopupItem>
                            </ErrorBoundary>
                        )}
                        nextSrc={getNextSlide().src ? getNextSlide().src : getNextSlide().pid}
                        nextCustomContent={(
                            <ErrorBoundary
                                place={'SlidesPopup-SlidesPopupItem-2'}
                                key={getNextSlide()?.pid}>
                                <SlidesPopupItem
                                    data={getNextSlide()}
                                    onDeleteSlide={(e: React.MouseEvent<HTMLElement>) => onDeleteSlide(e, getNextSlide())}
                                    onRotateSlide={(e: React.MouseEvent<HTMLElement>) => onRotateSlide(e, getNextSlide())}
                                    onEditSlide={(e: React.MouseEvent<HTMLElement>) => onEditSlide(e, getNextSlide())}
                                >
                                </SlidesPopupItem>
                            </ErrorBoundary>
                        )}
                        prevSrc={getPrevSlide().src ? getPrevSlide().src : getPrevSlide().pid}
                        prevCustomContent={(
                            <ErrorBoundary
                                place={'SlidesPopup-SlidesPopupItem-3'}
                                key={getPrevSlide()?.pid}>
                                <SlidesPopupItem
                                    data={getPrevSlide()}
                                    onDeleteSlide={(e: React.MouseEvent<HTMLElement>) => onDeleteSlide(e, getPrevSlide())}
                                    onRotateSlide={(e: React.MouseEvent<HTMLElement>) => onRotateSlide(e, getPrevSlide())}
                                    onEditSlide={(e: React.MouseEvent<HTMLElement>) => onEditSlide(e, getPrevSlide())}
                                >
                                </SlidesPopupItem>
                            </ErrorBoundary>
                        )}
                        onCloseRequest={() => onCloseRequest()}
                        onMovePrevRequest={() => {
                            setCurrentSlide(getPrevSlide())
                        }}
                        onMoveNextRequest={() => {
                            setCurrentSlide(getNextSlide())
                        }}
                        enableZoom={false}
                    />
                ) : ''}
        </div>
    )
}

export default SlidesPopup;